import { renommerSalles } from './salles.js'

/**
 * « ACTUALISER » — calculs purs (testés sans navigateur), 25/10/2026.
 *
 * Contrat : CONTRAT-GESTION §6 de Sonde, schémas `DeviceRefreshResult`,
 * `ZoneRefreshResult` et `AlertRefreshResult` (packages/contracts/src/sonde/v1/).
 * La réponse devient ce que la carte affiche :
 *
 *   · la phrase de Sonde TELLE QUELLE (`message_fr`), puis son conseil
 *     (`advice_fr`) quand la phrase ne le porte pas déjà ;
 *   · le détail repliable : relevés (`run.collectors[].summary_fr`), témoins
 *     (`witnesses[].detail_fr`), sources (« État inconnu » quand Sonde n'en
 *     sait rien encore, jamais « OK »), alertes et leur `closing_fr` ;
 *   · une salle : le décompte, et les appareils absents ou qui ont changé ;
 *   · une alerte : son état après l'actualisation.
 *
 * Aucune déduction : qu'une alerte se ferme ou reste ouverte se lit dans
 * `closing_fr`, jamais dans `present`. Les anciens noms de salle sont
 * remplacés (ROOM 1…), comme partout dans la supervision — mais jamais dans
 * un nom d'appareil (« LumiNode Salon » reste tel quel) : chaque phrase de
 * Sonde commence par le nom de l'appareil.
 */

export type CibleActualisation =
  | { kind: 'device'; uid: string; nom: string }
  | { kind: 'zone'; code: string; nom: string }
  | { kind: 'alert'; id: string; nom: string }

/** Les motifs du relais (supervision-views.ts) et de Sonde. */
const UID = /^[a-z0-9][a-z0-9-]{1,62}$/
const ZONE = /^[A-Z0-9][A-Z0-9-]{0,31}$/
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function cibleValide(c: CibleActualisation): boolean {
  if (c.kind === 'device') return UID.test(c.uid)
  if (c.kind === 'zone') return ZONE.test(c.code)
  return UUID.test(c.id)
}

/** Une actualisation à la fois par cible : la clé de la cible. */
export function cleCible(c: CibleActualisation): string {
  if (c.kind === 'device') return `device:${c.uid}`
  if (c.kind === 'zone') return `zone:${c.code}`
  return `alert:${c.id.toLowerCase()}`
}

/** Chemin du relais, sans `/v1/` (POST /api/v1/supervision/v/<chemin>). */
export function cheminActualiser(c: CibleActualisation): string {
  if (c.kind === 'device') return `devices/${encodeURIComponent(c.uid)}/refresh`
  if (c.kind === 'zone') return `zones/${encodeURIComponent(c.code)}/refresh`
  return `alerts/${encodeURIComponent(c.id.toLowerCase())}/refresh`
}

/** La clé Sonde d'une ligne du centre d'activité : seule une ALERTE s'actualise, pas un incident. */
const CLE_ALERTE_SONDE = /^sonde:alert:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i
export function alerteSondeDe(externalAlertId: string): string | null {
  return CLE_ALERTE_SONDE.exec(externalAlertId)?.[1]?.toLowerCase() ?? null
}

/* ═══════════════ la réponse ═══════════════ */

export interface ResultatActualisation {
  phrase: string
  conseil: string | null
  /** Déjà actualisé il y a peu : la phrase le dit, ce n'est pas une erreur. */
  limite: boolean
  /** Un appareil dont l'état a changé pendant l'actualisation : « Hors ligne → En ligne ». */
  changement: string | null
  /** Une salle : son décompte en une phrase. */
  comptes: string | null
  /** Une salle : les appareils absents ou qui ont changé, leur phrase. */
  appareils: string[]
  /** Une alerte actualisée : son état après. */
  alerte: { titre: string; etat: string; fermeture: string | null } | null
  temoins: { libelle: string; texte: string; present: boolean }[]
  /** Alertes de l'appareil : titre et ce qui la ferme (closing_fr). */
  alertes: { titre: string; fermeture: string }[]
  releves: string[]
  sources: { libelle: string; etat: string }[]
}

type Obj = Record<string, unknown>
const objet = (x: unknown): x is Obj => typeof x === 'object' && x !== null && !Array.isArray(x)
const texte = (x: unknown): string | null => (typeof x === 'string' && x.trim() !== '' ? x : null)
const liste = (x: unknown): Obj[] => (Array.isArray(x) ? x.filter(objet) : [])
const nombre = (x: unknown): number => (typeof x === 'number' && Number.isFinite(x) ? x : 0)

/**
 * renommerSalles HORS des noms d'appareils cités : « LumiNode Salon : absent.
 * … la salle R1 … » → « LumiNode Salon : absent. … la salle ROOM 1 … ».
 */
export function renommerHorsNoms(t: string, noms: readonly string[]): string {
  const cites = [...new Set(noms.filter((n) => n.length > 0 && t.includes(n)))].sort((a, b) => b.length - a.length)
  if (cites.length === 0) return renommerSalles(t)
  const rx = new RegExp(cites.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'), 'g')
  let out = ''
  let i = 0
  for (const m of t.matchAll(rx)) {
    out += renommerSalles(t.slice(i, m.index)) + m[0]
    i = m.index + m[0].length
  }
  return out + renommerSalles(t.slice(i))
}

/** « 12 s », « 4 min », « 1h27 » : l'âge d'un dernier signe. */
export function dureeCourte(s: number): string {
  const n = Math.max(0, Math.round(s))
  if (n < 60) return `${n} s`
  const m = Math.floor(n / 60)
  if (m < 60) return `${m} min`
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`
}

const pluriel = (n: number, un: string, plusieurs: string) => (n > 1 ? plusieurs : un)

/** « 12 appareils : 10 présents, 1 absent, 1 éteint comme prévu. 2 ont changé d'état. » */
export function phraseComptes(c: unknown): string | null {
  if (!objet(c)) return null
  const total = nombre(c['total'])
  if (total === 0) return 'Aucun appareil suivi ici.'
  const present = nombre(c['present'])
  const absent = nombre(c['absent'])
  const eteint = nombre(c['off_expected'])
  const inconnu = nombre(c['unknown'])
  const change = nombre(c['changed'])
  const parts = [`${present} ${pluriel(present, 'présent', 'présents')}`]
  if (absent) parts.push(`${absent} ${pluriel(absent, 'absent', 'absents')}`)
  if (eteint) parts.push(`${eteint} ${pluriel(eteint, 'éteint', 'éteints')} comme prévu`)
  if (inconnu) parts.push(`${inconnu} ${pluriel(inconnu, 'indéterminé', 'indéterminés')}`)
  let t = `${total} ${pluriel(total, 'appareil', 'appareils')} : ${parts.join(', ')}.`
  if (change) t += ` ${change} ${pluriel(change, 'a changé', 'ont changé')} d’état pendant l’actualisation.`
  return t
}

function temoinsDe(device: unknown, salles: (t: string) => string): ResultatActualisation['temoins'] {
  if (!objet(device)) return []
  return liste(device['witnesses']).map((w) => {
    const present = w['present'] === true
    const age = typeof w['age_s'] === 'number' ? ` (${w['passive'] === true ? 'dernier signe' : 'relevé'} il y a ${dureeCourte(w['age_s'])})` : ''
    return {
      libelle: texte(w['label']) ?? texte(w['source_label']) ?? 'Témoin',
      texte: salles(texte(w['detail_fr']) ?? (present ? 'le voit' : 'ne le voit pas')) + age,
      present,
    }
  })
}

function alertesDe(device: unknown, salles: (t: string) => string, sauf: string | null): ResultatActualisation['alertes'] {
  if (!objet(device)) return []
  return liste(device['alerts']).filter((a) => sauf === null || a['id'] !== sauf).map((a) => ({
    titre: salles(texte(a['title']) ?? 'Alerte'),
    // closing_fr d'abord : il vient de l'état relu ; sinon l'état tel que Sonde le nomme.
    fermeture: salles(texte(a['closing_fr']) ?? texte(a['state_label']) ?? '—'),
  }))
}

/** Statut null : la source n'a encore rien dit. Jamais « OK » par défaut. */
const etatSource = (s: Obj): string => (s['status'] === null || s['status'] === undefined ? 'État inconnu' : texte(s['status_label']) ?? 'État inconnu')

function sourcesDe(run: Obj | null): ResultatActualisation['sources'] {
  if (!run) return []
  return liste(run['sources']).map((s) => ({ libelle: texte(s['label']) ?? texte(s['code']) ?? 'Source', etat: etatSource(s) }))
}

/** « Hors ligne → En ligne », seulement si l'état a changé pendant l'actualisation. */
function changementDe(device: unknown): string | null {
  if (!objet(device) || device['changed'] !== true || !objet(device['before']) || !objet(device['after'])) return null
  const avant = texte(device['before']['display_label'])
  const apres = texte(device['after']['display_label'])
  return avant && apres ? `${avant} → ${apres}` : null
}

/** Les noms d'appareils cités par la réponse : jamais renommés. */
function nomsDe(...appareils: unknown[]): string[] {
  return appareils.flatMap((d) => (objet(d) && objet(d['device']) && texte(d['device']['display_name']) ? [texte(d['device']['display_name'])!] : []))
}

/**
 * La réponse d'une actualisation, ou null si elle n'a pas la forme du
 * contrat (la carte le dit plutôt que d'afficher un état faux).
 */
export function lireResultat(kind: CibleActualisation['kind'], x: unknown): ResultatActualisation | null {
  if (!objet(x) || !objet(x['target'])) return null
  const phrase = texte(x['message_fr'])
  if (!phrase) return null
  const run = objet(x['run']) ? x['run'] : null
  if (kind !== 'alert' && !run) return null
  const a = x['alert']
  if (kind === 'alert' && !objet(a)) return null

  const device = kind === 'zone' ? null : x['device']
  const zone = kind === 'zone' ? x : kind === 'alert' && objet(x['zone']) ? x['zone'] : null
  const membres = zone ? liste(zone['devices']) : []
  const noms = nomsDe(device, ...membres)
  const salles = (t: string): string => renommerHorsNoms(t, noms)
  // La phrase de l'appareil porte déjà son conseil à la fin : il n'est pas répété.
  const conseilBrut = texte(x['advice_fr'])
  const conseil = conseilBrut && !phrase.includes(conseilBrut) ? salles(conseilBrut) : null

  const alerte: ResultatActualisation['alerte'] = kind === 'alert' && objet(a)
    ? { titre: salles(texte(a['title']) ?? 'Alerte'), etat: texte(a['state_label']) ?? '—', fermeture: texte(a['closing_fr']) ? salles(texte(a['closing_fr'])!) : null }
    : null
  const sources = sourcesDe(run)
  // Alerte de source : l'état relu de la source, même quand rien n'a été relancé.
  const src = kind === 'alert' && objet(x['source']) ? x['source'] : null
  if (src && !sources.some((s) => s.libelle === (texte(src['label']) ?? texte(src['code'])))) {
    sources.push({ libelle: texte(src['label']) ?? texte(src['code']) ?? 'Source', etat: etatSource(src) })
  }

  return {
    phrase: salles(phrase),
    conseil,
    limite: run?.['throttled'] === true,
    changement: changementDe(device),
    comptes: zone ? phraseComptes(zone['counts']) : null,
    appareils: membres.map((d) => texte(d['message_fr'])).filter((t): t is string => t !== null).map(salles),
    alerte,
    temoins: temoinsDe(device, salles),
    // L'alerte actualisée est déjà dite plus haut : pas une seconde fois parmi celles de l'appareil.
    alertes: alertesDe(device, salles, kind === 'alert' && objet(a) ? texte(a['id']) : null),
    // Une ligne par relevé : Sonde ne nomme pas le relevé dans `summary_fr` (« relancé, terminé »).
    releves: run ? liste(run['collectors']).flatMap((c) => {
      const t = texte(c['summary_fr'])
      return t ? [`${texte(c['label']) ?? texte(c['source_label']) ?? texte(c['code']) ?? 'Relevé'} : ${salles(t)}`] : []
    }) : [],
    sources,
  }
}

export const aDuDetail = (r: ResultatActualisation): boolean =>
  r.changement !== null || r.comptes !== null || r.appareils.length > 0 || r.alerte !== null || r.temoins.length > 0
  || r.alertes.length > 0 || r.releves.length > 0 || r.sources.length > 0

/* ═══════════════ les refus ═══════════════ */

/**
 * Le message d'un refus. `parDefaut` est celui de `messageEchecMutation`,
 * qui ne garde pas le texte d'un 403 : « Actualiser » coupé (REFRESH_DISABLED)
 * dit où le rallumer, il est rendu tel quel.
 */
export function messageEchecActualiser(status: number, data: unknown, parDefaut: string): string {
  const code = objet(data) ? texte(data['error']) : null
  const message = objet(data) ? texte(data['message']) : null
  if (code === 'REFRESH_DISABLED') return message ?? '« Actualiser » est coupé dans les réglages de la supervision.'
  if (code === 'COLLECTOR_UNAVAILABLE') {
    const m = message ?? 'Le collecteur de Sonde ne répond pas.'
    return /réessay/i.test(m) ? m : `${m} Réessayez dans un instant.`
  }
  // Sonde injoignable ou trop lente : « la modification a pu être enregistrée » ne vaut pas ici, rien ne se modifie.
  if (code === 'SONDE_UNAVAILABLE') return 'Sonde n’a pas répondu à temps. Réessayez dans un instant : une actualisation ne change aucun réglage.'
  if (status === 404) return message ? renommerSalles(message) : 'Introuvable dans Sonde.'
  return parDefaut
}
